import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import Swal from 'sweetalert2';
import axios from 'axios';
import { setBackButtonUrl } from '../uiSlice';
import './photobooth.css';

const API = process.env.REACT_APP_PHOTOBOOTH_API_URL;

export default function PhotoboothFinal() {
  const { user }            = useSelector(state => state.auth);
  const { data: themeData } = useSelector(state => state.theme);
  const navigate            = useNavigate();
  const dispatch            = useDispatch();

  const [imageData, setImageData] = useState(null);
  const [loading,   setLoading]   = useState(true);

  const authHeader = () => ({
    Authorization:  `Bearer ${user?.sessionId}&${user?.organizationId}`,
    'Content-Type': 'application/json',
  });

  // The shared header's Back button reads this from Redux (see dddUI/user.jsx)
  // — it defaults to null/empty, which made Back a no-op reload on this page.
  useEffect(() => {
    dispatch(setBackButtonUrl('/avatar'));
  }, [dispatch]);

  // The <a download> attribute is ignored by browsers for cross-origin URLs
  // (the S3 image URL isn't same-origin), so it was just opening the image
  // in a new tab instead of actually saving it — worse on mobile, where
  // there's no "right-click > Save As" fallback at all. Fetch the image as
  // a blob instead: on mobile, hand it to the native share sheet so "Save
  // Image"/"Save to Photos" actually saves to the device gallery; on
  // desktop (or if sharing isn't available), trigger a real same-origin
  // blob download so it lands in the Downloads folder.
  const handleDownload = async () => {
    if (!imageData?.imageUrl) return;
    try {
      const resp = await fetch(imageData.imageUrl);
      const blob = await resp.blob();
      const file = new File([blob], 'my-superhero-avatar.jpg', { type: blob.type || 'image/jpeg' });

      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: 'My Superhero Avatar' });
        return;
      }

      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = 'my-superhero-avatar.jpg';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(blobUrl);
    } catch (err) {
      if (err?.name === 'AbortError') return; // user cancelled the share sheet
      // Last resort: open it so the user can long-press/right-click save manually.
      window.open(imageData.imageUrl, '_blank', 'noreferrer');
    }
  };

  useEffect(() => {
    if (!user?.userId) return;
    axios
      .post(
        `${API}/api/photobooth/final`,
        { userId: user.userId },
        { headers: authHeader() },
      )
      .then(res => {
        setImageData(res.data);
        console.log('Final image data:', res.data);
      })
      .catch(() =>
        Swal.fire('Error', 'Could not load your final image.', 'error').then(() =>
          navigate('/avatar'),
        ),
      )
      .finally(() => setLoading(false));
  }, [user?.userId]);

  const isMobile = window.innerWidth <= 768;
  const bgImg    = themeData
    ? (isMobile ? themeData.background_mob : themeData.background_desk)
    : null;
  const bgStyle  = bgImg
    ? {
        backgroundImage:      `url("${process.env.REACT_APP_S3_PATH}${bgImg}")`,
        backgroundSize:       '100% 100%',
        backgroundRepeat:     'no-repeat',
        backgroundAttachment: 'fixed',
      }
    : {};

  if (loading) {
    return (
      <div className="pb-page pb-center" style={bgStyle}>
        <div className="pb-loader" />
      </div>
    );
  }

  return (
    <div className="pb-page pb-center" style={bgStyle}>
      <div className="pb-final-box">
        <h2 className="pb-title" style={{ color: themeData?.text_color || '#FFD94A' }}>
          {themeData?.follow_text || 'Here is your Superhero Avatar! 🎉'}
        </h2>

        {imageData?.imageUrl ? (
          <div className="pb-final-img-wrap">
            <img
              src={imageData.imageUrl}
              alt="Your Superhero avatar"
              className="pb-final-img"
            />
          </div>
        ) : (
          <p style={{ color: themeData?.text_color || '#ccc', padding: '20px 0' }}>Image not available.</p>
        )}

        <div className="pb-btn-row" style={{ justifyContent: 'center' }}>
          {imageData?.imageUrl && (
            <button
              className="pb-btn"
              onClick={handleDownload}
              style={{
                background: themeData?.button_background
                  ? themeData.button_background
                  : `linear-gradient(180deg, ${themeData?.button_color}, ${themeData?.button_color})`,
                color: themeData?.button_text_color ? themeData.button_text_color : '#fff',
              }}
            >
              ⬇ Download
            </button>
          )}
          {themeData?.gallery_enabled !== false && (
            <button
              className="pb-btn-secondary"
              onClick={() => navigate('/avatar')}
              style={{
                borderColor: themeData?.button_color,
                color: themeData?.button_color,
              }}
            >
              View Gallery
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
